/**
 * The Prospects page (PRD Phase 1 advisor dashboard): the advisor's CRM contacts, ranked by priority
 * for a long-term care conversation, with the reasons behind each and a way to say whether the list
 * is right.
 *
 * The contacts shown are from the CRM prospecting is active for (`useProspectingProviders`). An
 * advisor whose only CRM is paused is told so; one with no CRM for it yet is sent to Integrations.
 *
 * The list is the point of the page, so everything above it is kept short. Top to bottom: the title
 * with its statuses (work in progress, data quality, a preview model), one banner at most when
 * something needs acting on, the three priorities as compact cards, the other views as tabs, filters,
 * the ranked table, and the per-prospect panel. What the page says about the state of the book is
 * decided in `lib/book-notices`.
 *
 * View, filters, page and the open prospect live in the URL. Data and effects are in the hooks; this
 * component only lays them out.
 */
import type { ReactNode } from 'react';
import { Skeleton, Spinner } from '@design-system';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { useAdvisorNavigation } from '@/hooks/useAdvisorNavigation';
import { useProspectingProviders } from '@/hooks/useProspectingProviders';
import { crmName } from '@/util/crmNames';
import { useProspectDetail } from '../hooks/use-prospect-detail';
import { useProspectFilters } from '../hooks/use-prospect-filters';
import { useProspects } from '../hooks/use-prospects';
import { useProspectsAnalytics } from '../hooks/use-prospects-analytics';
import { bookNotices } from '../lib/book-notices';
import { priorityCopy } from '../lib/labels';
import { BookBanner } from './book-banner';
import { BookStatusChips } from './book-status-chips';
import { MethodologySheet } from './methodology-sheet';
import { ProspectPanel } from './prospect-panel';
import { ProspectsFilters } from './prospects-filters';
import { ProspectsStatus } from './prospects-status';
import { ProspectsTable } from './prospects-table';
import { ScoringSettingsSheet } from './scoring-settings-sheet';
import { TierSummary } from './tier-summary';

/** The same page padding as the other advisor dashboard pages. */
const PAGE = 'px-4 pb-12 pt-3 sm:px-6 lg:px-8 flex flex-col gap-4';

export function ProspectsPage() {
  const filters = useProspectFilters();
  const providers = useProspectingProviders();
  const provider = providers.active?.provider ?? null;
  const prospects = useProspects(provider, filters.query);
  const { navigateToIntegrations, navigateToCrmFieldMap } = useAdvisorNavigation();
  const { detail } = useProspectDetail(provider, filters.openProspectId);
  const book = prospects.data?.book;
  useProspectsAnalytics(book?.scoring.modelVersion, detail);

  const crm = provider ? crmName(provider) : null;
  const notices = book ? bookNotices(book) : null;
  const openFieldMapping = () => (provider ? navigateToCrmFieldMap(provider) : navigateToIntegrations());

  // Wrapped so the header and its divider are one block: as two, the page's gap would apply twice.
  // The divider brings its own space below, so the wrapper gives some of the page's gap back.
  const pageHeader = (description: ReactNode = priorityCopy.intro(crm ?? 'CRM')) => (
    <div className="-mb-2">
    <DashboardPageHeader
      title="Prospects"
      className="my-2"
      titleAddon={
        notices &&
        crm && (
          <BookStatusChips
            crmName={crm}
            notices={notices}
            onOpenFieldMapping={openFieldMapping}
          />
        )
      }
      description={description}
      actions={
        book &&
        provider && (
          <div className="flex gap-2">
            <MethodologySheet book={book} />
            <ScoringSettingsSheet provider={provider} />
          </div>
        )
      }
    />
    </div>
  );

  if (providers.isLoading) {
    return (
      <div className={PAGE}>
        {pageHeader()}
        <LoadingProspects />
      </div>
    );
  }

  if (!provider || !crm) {
    const [paused] = providers.paused;
    return (
      <div className={PAGE}>
        {pageHeader()}
        {providers.isError ? (
          <ProspectsStatus kind="temporarilyUnavailable" onRetry={() => providers.refetch()} />
        ) : paused ? (
          <ProspectsStatus kind="paused" crmName={paused.name} />
        ) : (
          <ProspectsStatus kind="notAvailable" onGoToIntegrations={navigateToIntegrations} />
        )}
      </div>
    );
  }

  if (prospects.unavailable) {
    return (
      <div className={PAGE}>
        {pageHeader()}
        <ProspectsStatus
          kind={prospects.unavailable}
          crmName={crm}
          message={prospects.unavailableMessage}
          onGoToIntegrations={navigateToIntegrations}
          onRetry={() => prospects.refetch()}
        />
      </div>
    );
  }

  if (prospects.isLoading || !prospects.data || !book || !notices) {
    return (
      <div className={PAGE}>
        {pageHeader()}
        <LoadingProspects />
      </div>
    );
  }

  const { items, page, pageSize, total } = prospects.data;
  const view = filters.query.view ?? 'tiered';

  return (
    <div className={PAGE}>
      {pageHeader(book.intentDataAvailable ? priorityCopy.intro(crm) : priorityCopy.introDetailsOnly(crm))}

      <BookBanner
        book={book}
        notices={notices}
        onOpenFieldMapping={openFieldMapping}
        onOpenIntegrations={navigateToIntegrations}
      />

      {!book.scoring.lastScoredAt ? (
        <ProspectsStatus
          kind={book.scoring.waitingForFirstSync ? 'waitingForFirstSync' : 'notScoredYet'}
          crmName={crm}
          onGoToIntegrations={navigateToIntegrations}
        />
      ) : (
        <>
          <TierSummary
            book={book}
            view={view}
            selectedTier={filters.query.tier}
            onViewChange={filters.setView}
            onTierSelect={tier => {
              if (view !== 'tiered') filters.setView('tiered');
              filters.setFilter('tier', tier);
            }}
          />
          <ProspectsFilters
            query={filters.query}
            hasFilters={filters.hasFilters}
            onChange={filters.setFilter}
            onClear={filters.clearFilters}
          />
          {items.length === 0 && page > 1 && total > 0 ? (
            <ProspectsStatus kind="pastLastPage" onFirstPage={() => filters.setPage(1)} />
          ) : items.length === 0 ? (
            <ProspectsStatus kind="emptyView" onClearFilters={filters.hasFilters ? filters.clearFilters : undefined} />
          ) : (
            <ProspectsTable
              items={items}
              crmName={crm}
              view={view}
              page={page}
              pageSize={pageSize}
              total={total}
              isRefreshing={prospects.isRefreshing}
              onOpen={filters.openProspect}
              onPageChange={filters.setPage}
            />
          )}
        </>
      )}

      <ProspectPanel
        provider={provider}
        prospectId={filters.openProspectId}
        modelVersion={book.scoring.modelVersion}
        onClose={() => filters.openProspect(null)}
      />
    </div>
  );
}

function LoadingProspects() {
  return (
    <div
      role="status"
      className="flex flex-col gap-4"
    >
      <p className="flex items-center gap-2 text-sm text-gray-700">
        <Spinner aria-hidden="true" />
        Loading your prospects…
      </p>
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
